import { getIdToken } from "@/auth/cognito";
import type {
  CreateTenantRequest,
  CreateTenantResponse,
  ListTenantsResponse,
  Tenant,
  TenantStatus,
} from "@/types";

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL;

class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, "Not authenticated");

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
      ...(init.headers ?? {}),
    },
  });

  const text = await res.text();
  const body = text ? safeParse(text) : undefined;

  if (!res.ok) {
    const message =
      (typeof body === "object" && body !== null && "message" in body
        ? (body as { message?: string }).message
        : undefined) ?? `Request failed: ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// -----------------------------------------------------------------------------
// Tenants API
// -----------------------------------------------------------------------------
export const tenantsApi = {
  list: (params?: { status?: TenantStatus; nextToken?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.append("status", params.status);
    if (params?.nextToken) qs.append("nextToken", params.nextToken);
    if (params?.limit) qs.append("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<ListTenantsResponse>(`/tenants${suffix}`);
  },

  get: (tenantId: string) => request<Tenant>(`/tenants/${tenantId}`),

  create: (payload: CreateTenantRequest) =>
    request<CreateTenantResponse>("/tenants", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  delete: (tenantId: string) =>
    request<{ tenantId: string; status: TenantStatus }>(`/tenants/${tenantId}`, {
      method: "DELETE",
    }),

  suspend: (tenantId: string) =>
    request<{ tenantId: string; status: TenantStatus }>(
      `/tenants/${tenantId}/suspend`,
      { method: "POST" }
    ),

  resume: (tenantId: string) =>
    request<{ tenantId: string; status: TenantStatus }>(
      `/tenants/${tenantId}/resume`,
      { method: "POST" }
    ),
};

export { ApiError };
